import { defineStore as define } from 'pinia'
import { useRouter as use_router } from 'vue-router'
import {
  auth,
  ref_doc,
  set_doc,
  doc,
  list,
  listen,
  load,
  update,
  where,
  order_by,
  limit,
  query,
  sign_out,
  users as users_ref,
  auth_changed,
  savable_date
} from '@/utils/serverless'
import { hydrate_list, debounce, roles_check } from '@/utils/common'
import { ref, computed } from 'vue'
import type {
  User,
  User_Payload,
  Active_Status,
  User_Roles,
  Address,
  Social,
  Participant,
  Demographics,
  Mini_Project
} from '@/types'

const make_initials = (name: string) => {
  if (!name) return ''

  const rgx = new RegExp(/(\p{L}{1})\p{L}+/u, 'gu')
  const initials = [...name.matchAll(rgx)]
  return (
    (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
  ).toUpperCase()
}

const create_user = async (pyld: User_Payload) => {
  const ref = ref_doc(users_ref)
  const new_user = {
    active: 'New' as Active_Status,
    access: ['Participant'] as User_Roles[],
    demographics: {} as Demographics,
    phone: pyld.phone,
    address: {
      country: pyld.country
    } as Address,
    social: {} as Social,
    name: undefined,
    username: undefined,
    photos: undefined,
    created: savable_date(),
    updated: savable_date(),
    id: ref.id
  } as User

  await set_doc(ref, new_user)
  return new_user
}

const check_username_status = async (username: string) => {
  if (!username) return 'unavailable'
  const snap = await list(by_username(username))
  return snap.empty ? 'clean' : 'unavailable'
}

const check_email_status = async (email: string) => {
  const existing_email = query(users_ref, where('email', '==', email))
  const snap = await list(existing_email)
  return snap.empty
}

const add_participant_to_project = async (
  parp_id: string,
  project_id: string
) => {
  const participant_ref = doc(`projects/${project_id}/participants/${parp_id}`)
  const user_data = await load(doc(`users/${parp_id}`))

  const participant = {
    status: 'approved',
    user: doc(`users/${parp_id}`),
    demographics: user_data.data()?.demographics || {},
    address: user_data.data()?.address || {},
    username: user_data.data()?.username || '',
    created: savable_date(),
    updated: savable_date()
  } as Participant

  await set_doc(participant_ref, participant)
}

export const me = ref<User | null | undefined>(undefined)
export const auth_ready = ref(false)

const log_custom_claims = async (user: any) => {
  const token_result = await user.getIdTokenResult()
  console.log('Custom claims:', token_result.claims)

  const expected_custom_claims = [
    'admin',
    'user_id',
    'companies',
    'researcher',
    'sponsor',
    'manager'
  ]

  const missing_claims = expected_custom_claims.filter(
    claim => !(claim in token_result.claims)
  )

  if (missing_claims.length > 0) {
    console.log('Missing custom claims:', missing_claims)
  }
}

const handle_auth_change = async (user: any) => {
  if (!user) {
    me.value = null
    auth_ready.value = true
    return
  }

  await auth.currentUser?.getIdToken(true)
  const phone_query = query(users_ref, where('phone', '==', user.phoneNumber))
  const snap = await list(phone_query)

  if (snap.empty) {
    me.value = await create_user({
      country: user.country,
      phone: user.phoneNumber
    })
  } else {
    me.value = snap.docs[0].data() as User
  }

  listen(
    doc(`users/${me.value.id}`),
    changed => (me.value = changed.data() as User)
  )

  auth_ready.value = true
}

export const auth_initialized = new Promise<void>(resolve => {
  auth_changed(auth, async user => {
    await handle_auth_change(user)
    resolve()
  })
})

export const use_user = define('user', () => {
  const users = ref<User[] | null>(null)
  const user = ref<User | null>(null)
  const unique_name = ref('unavailable')
  const unique_email = ref(true)
  const photos = ref(null)
  const projects = ref<Mini_Project[] | null>(null)
  const router = use_router()

  const roles = (check: string[]) => roles_check(me.value, check)

  const load_user = async (pyld: { id: string }) => {
    const snap = await load(doc(`users/${pyld.id}`))
    if (snap.exists()) user.value = snap.data() as User
  }

  const get_user = async (id: string) => {
    const snap = await load(doc(`users/${id}`))
    if (snap.exists()) {
      return snap.data() as User
    }
    return null
  }

  const update_avatar = async pyld => {
    if (!pyld.id.empty) update(doc(`users/${pyld.id}`), { avatar: pyld.url })
  }

  const update_user = async (pyld: User_Payload) => {
    const a_user = pyld.user
    if (a_user?.id) {
      await update(doc(`users/${a_user.id}`), a_user)
      user.value = a_user
    }
  }

  const username_status = async (pyld: User_Payload) => {
    unique_name.value = await check_username_status(pyld?.username)
  }

  const email_status = async (email: string) => {
    unique_email.value = await check_email_status(email)
  }

  const signout = async () => {
    await sign_out()
    router.push('/')
    me.value = null
  }

  const initials = computed(() => make_initials(me.value.name))

  const load_new_users = async () => {
    const recent = query(
      users_ref,
      where('active', '==', 'Y'),
      limit(100),
      order_by('updated', 'desc')
    )
    const snap = await list(recent)
    users.value = hydrate_list(snap) as User[]
  }

  const search_usernames = (search: string) => {
    debounce(async () => {
      const starts_with = query(
        users_ref,
        where('username', '>=', search),
        where('username', '<=', `${search}\uf8ff`)
      )
      const snap = await list(starts_with)
      users.value = [...users.value, ...(hydrate_list(snap) as User[])]
    })
  }

  const home_base = () => {
    if (roles(['Sponsor', 'Recruiter', 'Researcher']))
      router.replace('/projects')
    else router.replace('/participants')
  }

  return {
    users,
    user,
    me,
    unique_name,
    unique_email,
    projects,
    photos,
    initials,
    make_initials,
    roles,
    home_base,
    add_participant_to_project,
    load_user,
    get_user,
    update_avatar,
    update_user,
    username_status,
    email_status,
    signout,
    load_new_users,
    search_usernames
  }
})

export const by_username = (username: string) =>
  query(users_ref, where('username', '==', username))

export const by_email = (email: string) =>
  query(users_ref, where('email', '==', email))
